// Shared types between frontend (React) and backend (Azure Functions).
// Mirrors db/schema.sql.

export type RxStatus =
  | "matched"
  | "tech_review"
  | "on_hold"
  | "ready_for_pk"
  | "pk_filing"
  | "imported_to_pk"
  | "pk_error_review"
  | "rejected"
  | "archived";
export type HoldReason = "prescriber_callback" | "insurance" | "patient_callback" | "other";
export type DocumentType = "new_rx" | "refill_authorization" | "transfer" | "other";
export type IntakeSource = "updox" | "sharepoint" | "upload";

// Intake mode — the SPA and API both branch on this.
// "rx"      → prescription intake, target PMS = PK Software (all 11 stores)
// "vaccine" → vaccine consent form, target PMS = PioneerRx (Community sites only)
export type IntakeMode = "rx" | "vaccine";
export type TargetPms = "pk" | "pioneerrx" | "recomrx";

// User-facing labels for each target PMS. Used in dropdowns + review page chips.
export const TARGET_PMS_LABEL: Record<TargetPms, string> = {
  pk:        "PK Software",
  pioneerrx: "PioneerRx",
  recomrx:   "ReComRx",
};

// Which PMSs each store is allowed to send to.
// Community (COR/DEN/FM) → PioneerRx + ReComRx
// Everyone else → PK + ReComRx
// After full ReComRx cutover we simplify to just ReComRx.
export function allowedPmsForStore(storeCode: string): TargetPms[] {
  const community = new Set(["COR", "DEN", "FM"]);
  return community.has(storeCode) ? ["pioneerrx", "recomrx"] : ["pk", "recomrx"];
}

// Vaccine codes match PioneerRx template shortcuts (typing tip #7).
// Prevnar/Boostrix/Shingrix/RSV have no shortcut — bot searches Item manually.
export type VaccineCode =
  | "fluzonehd"   // Fluzone HD    → =fluzonehd
  | "fluad"       // Fluad         → =fluad
  | "fluzone"    // Fluzone Tri   → =fluzone
  | "spike"       // Spikevax      → =spike
  | "pfi"         // Comirnaty     → =pfi
  | "mnex"        // mNEXSPIKE     → =mnex
  | "prevnar"     // Prevnar (Pneumo) — no shortcut
  | "boostrix"    // Boostrix TDAP — no shortcut, Part D only
  | "shingrix"    // Shingrix     — no shortcut, Part D only
  | "rsv";        // mResvia RSV  — no shortcut, Part D only

export type InsuranceBranch =
  | "advantage"    // Advantage plan → run through Part D Advantage (any vaccine ok)
  | "partb_card"   // Card has Part B pharmacy processing → run that card (Flu/COVID/Prevnar only)
  | "fdsmedical"   // No Advantage / no Part B card → FDSMEDICAL BIN 007895 (Flu/COVID/Prevnar only)
  | "tricare"      // Tricare → Tricare pharmacy benefits (any vaccine ok)
  | "railroad";    // Railroad Medicare → same as Part B but RR prefix on cardholder ID

export type AdminSite = "LD" | "RD";  // Left / Right Deltoid

// The set of vaccines that CAN go Part B (typing tip #12).
// Anything else forced onto partb_card/fdsmedical/railroad is a validation error.
export const PART_B_ELIGIBLE_VACCINES: ReadonlyArray<VaccineCode> = [
  "fluzonehd","fluad","fluzone","spike","pfi","mnex","prevnar",
];

// Template shortcut lookup — null means bot must search the Item field manually.
export const VACCINE_TEMPLATE_SHORTCUT: Record<VaccineCode, string | null> = {
  fluzonehd: "=fluzonehd",
  fluad:     "=fluad",
  fluzone:   "=fluzone",
  spike:     "=spike",
  pfi:       "=pfi",
  mnex:      "=mnex",
  prevnar:   null,
  boostrix:  null,
  shingrix:  null,
  rsv:       null,
};

// Labels match PioneerRx's Dispensed Item names from Community's recent claims report,
// so what the tech sees in review matches what the PAD will enter in PioneerRx.
export const VACCINE_LABEL: Record<VaccineCode, string> = {
  fluzonehd: "Fluzone High-Dose 2026-27 Syr",
  fluad:     "Fluad 2026-2027 Syringe",
  fluzone:   "Fluzone 2026-2027 Syringe",
  spike:     "Spikevax 2026-27 (12yr Up) 0.5 Ml Syringe",
  pfi:       "Comirnaty 2026-27 (12y Up) 0.3 Ml Syringe",
  mnex:      "Mnexspike 2026-2027 (12yr Up) 0.2 Ml Syringe",
  prevnar:   "Prevnar 20 Inj",
  boostrix:  "Boostrix Inj",
  shingrix:  "Shingrix 50 Mcg/0.5 Ml Syringe",
  rsv:       "Mresvia 50 Mcg/0.5 Ml Syringe",
};

// For the 4 vaccines without =shortcuts, the PAD searches PioneerRx's Item field
// by exact name. NDCs below are the specific products Community has been dispensing
// (confirmed from the recent claims report). PAD validates match on both name + NDC.
export const VACCINE_PIONEERRX_SEARCH: Partial<Record<VaccineCode, { name: string; ndc: string }>> = {
  prevnar:  { name: "Prevnar 20 Inj",                   ndc: "0005-2000-02" },
  boostrix: { name: "Boostrix Inj",                     ndc: "58160-0842-11" },  // -11 or -52, both named "Boostrix Inj"; PAD picks first match
  shingrix: { name: "Shingrix 50 Mcg/0.5 Ml Syringe",   ndc: "58160-0849-52" },
  rsv:      { name: "Mresvia 50 Mcg/0.5 Ml Syringe",    ndc: "80777-0345-61" },
};

export type QueueRow = {
  rxId: number;
  intakeId: number;
  storeCode: string;
  patientName: string;
  drug: string;
  isControlled: boolean;
  isCompound: boolean;
  species: string;
  rxConfidence: number | null;
  receivedAt: string;          // ISO 8601
  status: RxStatus;
  warningCount: number;
  recordType: IntakeMode;
  targetPms: TargetPms;
};

export type VaccineDetail = {
  rxId: number;
  vaccineCode: VaccineCode;
  vaccineLabel: string;                     // human-friendly label from VACCINE_LABEL
  templateShortcut: string | null;          // '=fluzonehd' or null
  lotNumber: string | null;
  adminSite: AdminSite | null;
  vaccinationDate: string;                  // ISO date — drives BOTH Written + Fill in PioneerRx
  visDate: string | null;
  facilityName: string;                     // 'Vaccine Clinic'
  facilityWing: string | null;              // e.g. 'Robson Ranch'
  facilityAdmittedOn: string | null;        // MUST be <= vaccinationDate
  medicareNumber: string | null;            // MBI e.g. 3W06XE3AN89
  ssnLast4: string | null;
  insuranceBranch: InsuranceBranch;
  fdsmedicalStartDate: string | null;       // Jan 1 of vaccination year
  railroadCardholderId: string | null;      // RR-prefixed
  consentFormSignedDate: string | null;
  consentFormSignaturePresent: boolean;
  pharmacyId: number;                       // 7=DEN, 8=FM, 9=COR (Community only)
  cardBin: string | null;                   // for partb_card / tricare: pharmacy processor BIN
  cardPcn: string | null;
  cardCardholderId: string | null;
  cardGroupNumber: string | null;
};

export type IntakeSummary = {
  intakeId: number;
  storeCode: string;
  source: IntakeSource;
  receivedAt: string;
  documentType: DocumentType | null;
  rxCount: number;
  pageCount: number | null;
};

export type PatientSearchResult = {
  patientPkId: number;
  storeId: number;
  lastName: string;
  firstName: string;
  middleName: string | null;
  dob: string | null;
  phone: string | null;
  species: string;
  active: boolean;
};

export type PrescriberSearchResult = {
  prescriberPkId: number;
  storeId: number;
  lastName: string;
  firstName: string | null;
  npi: string | null;
  dea: string | null;
  title: string | null;
  business: string | null;
  cannotWriteControlled: boolean;
};

// Full Rx detail returned by GET /api/rx/{id}.
export type RxDetail = {
  rxId: number;
  intakeId: number;
  extractionId: number;
  indexInDoc: number;
  totalRxInDoc: number;
  siblingRxIds: number[];
  status: RxStatus;
  statusUpdatedAt: string;
  recordType: IntakeMode;
  targetPms: TargetPms;
  vaccine: VaccineDetail | null;    // populated when recordType = 'vaccine'
  holdReason: HoldReason | null;
  holdNote: string | null;
  intake: {
    source: IntakeSource;
    blobPath: string;
    originalFilename: string | null;
    documentType: DocumentType | null;
    receivedAt: string;
    pageCount: number | null;
    documentUrl: string | null;
  };
  store: { code: string; name: string };
  patient: {
    nameRaw: string | null;
    dobRaw: string | null;
    phoneRaw: string | null;
    addressRaw: string | null;
    street: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    species: string;
    petName: string | null;
    matchConfidence: number | null;
    matched: {
      pkId: number; storeId: number;
      lastName: string; firstName: string;
      dob: string | null; phone: string | null;
    } | null;
  };
  prescriber: {
    nameRaw: string | null;
    npiRaw: string | null;
    deaRaw: string | null;
    phoneRaw: string | null;
    faxRaw: string | null;
    clinicRaw: string | null;
    matchConfidence: number | null;
    matched: {
      pkId: number; storeId: number;
      lastName: string; firstName: string | null;
      npi: string | null; dea: string | null;
      cannotWriteControlled: boolean;
    } | null;
  };
  prescription: {
    drug: string;
    genericName: string | null;
    strength: string | null;
    form: string | null;
    sig: string;
    quantity: string | null;
    qtyOnRefills: string | null;
    daysSupply: number | null;
    refills: number | null;
    refillIntervalDays: number | null;
    dawCode: number | null;
    rxOrigin: number;
    dateWritten: string | null;
    rxExpiration: string | null;
    counsellingReceived: boolean;
    rxNote: string | null;
    dispensingNote: string | null;
    billingNdc: string | null;
    shippingMethod: string | null;
  };
  controlled: {
    isControlled: boolean;
    schedule: "II" | "III" | "IV" | "V" | null;
    originalRequiredNote: string | null;
  };
  compound: {
    isCompound: boolean;
    base: string | null;
    notes: string | null;
    ingredients: {
      name: string;
      strengthOrPercent: string | null;
      quantity: string | null;
      isControlled: boolean;
      position: number;
    }[];
  };
  refillAuth: {
    response: string;
    newRefills: number | null;
    changesNote: string | null;
    signaturePresent: boolean;
  } | null;
  formula: {
    matchConfidence: number | null;
    speedCode: string | null;
    matched: {
      formulaId: number;
      storeId: number;
      name: string;
      form: string | null;
      strength: string | null;
      drugType: "C" | "B" | "G";
      schedule: string | null;
    } | null;
  };
  rxConfidence: number | null;
  warnings: string[];
};

export type HoldRow = {
  rxId: number;
  storeCode: string;
  patientName: string;
  drug: string;
  holdReason: HoldReason;
  holdNote: string | null;
  placedByName: string | null;
  placedByUserId: string | null;
  placedAt: string;            // ISO 8601
  receivedAt: string;          // ISO 8601 (intake)
  isControlled: boolean;
  isCompound: boolean;
};

export type RejectedRow = {
  rxId: number;
  recordType: IntakeMode;
  targetPms: TargetPms;
  rejectedAt: string;    // ISO 8601
  patientName: string;
  drug: string;
  storeCode: string;
  rejectedByUserId: string | null;
  rejectedByName: string | null;
  reason: string | null;
};

export type SearchResult = {
  rxId: number;
  recordType: IntakeMode;
  targetPms: TargetPms;
  status: RxStatus;
  statusUpdatedAt: string;
  holdReason: HoldReason | null;
  patientName: string;
  drug: string;
  storeCode: string;
};

export type ProcessingRow = {
  rxId: number;
  recordType: IntakeMode;
  targetPms: TargetPms;
  status: "ready_for_pk" | "pk_filing" | "pk_error_review";
  statusUpdatedAt: string;    // ISO 8601
  patientName: string;
  drug: string;
  rxConfidence: number | null;
  errorStep: string | null;
  errorDialog: string | null;
  storeCode: string;
};

export type ImportedRow = {
  rxId: number;
  storeCode: string;
  patientName: string;
  drug: string;
  filedAt: string;             // ISO 8601 — rx_output.output_at
  method: "file_import" | "pad_bridge" | "clipboard";
  targetPms: TargetPms | null;
  pkRxNumber: string | null;   // kept for back-compat; renders as "PMS Rx #"
  reviewedByName: string | null;
  reviewedByUserId: string | null;
  isControlled: boolean;
  isCompound: boolean;
};

export type ReportsSummary = {
  storeCode: string;
  generatedAt: string;
  statusCounts7d: Record<string, number>;
  statusCounts30d: Record<string, number>;
  throughput: { date: string; count: number }[];   // last 14 days, filed-per-day
  aiAccuracy: {
    rxReviewed: number;
    fieldsTotal: number;
    fieldsCorrected: number;
    accuracyPct: number;                            // 0–100
  };
  avgReviewSeconds: number | null;                  // null if no reviews
};

export type FormulaCandidate = {
  formulaId: number;
  storeId: number;
  name: string;
  form: string | null;
  strength: string | null;
  drugType: "C" | "B" | "G";
  schedule: string | null;
  speedCode: string | null;
  active: boolean;
  confidence: number;
  reasons: string[];
};
